import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { LogoHorizontal } from '../components/Logo';
import { PrimaryButton } from '../components/Button';
import { FormHeader } from '../components/Card';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function VerifyEmail() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const email = params.get('email') || '';
    const { refreshMe } = useAuth();

    const [digits, setDigits] = useState(['', '', '', '', '']);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const inputs = useRef([]);

    useEffect(() => { inputs.current[0]?.focus(); }, []);

    const setDigit = (i, val) => {
        const v = val.replace(/\D/g, '').slice(0, 1);
        const next = [...digits];
        next[i] = v;
        setDigits(next);
        if (v && i < 4) inputs.current[i + 1]?.focus();
    };

    const handleKeyDown = (i, e) => {
        if (e.key === 'Backspace' && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
    };

    const handlePaste = (e) => {
        const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 5);
        if (text.length === 5) {
            setDigits(text.split(''));
            inputs.current[4]?.focus();
            e.preventDefault();
        }
    };

    const submit = async (e) => {
        e?.preventDefault();
        const code = digits.join('');
        if (code.length !== 5) return setError('Ingresa el código de 5 dígitos');
        setError('');
        setLoading(true);
        try {
            await api.post('/auth/verify-email', { email, code });
            await refreshMe();
            navigate('/email-verificado', { replace: true });
        } catch (err) {
            const msg = formatApiError(err);
            if (msg.toLowerCase().includes('expir')) {
                navigate('/link-expirado', { replace: true });
            } else {
                navigate('/codigo-invalido', { replace: true });
            }
        } finally {
            setLoading(false);
        }
    };

    const resend = async () => {
        setResending(true);
        setError('');
        try {
            await api.post('/auth/resend-code', { email });
            setError('');
            alert('Te enviamos un código nuevo. Revisa tu correo (o la consola del backend en modo dev).');
        } catch (err) {
            setError(formatApiError(err));
        } finally { setResending(false); }
    };

    return (
        <div className="min-h-screen bg-surface app-shell flex flex-col py-8 px-6">
            <div className="flex justify-center mb-6">
                <LogoHorizontal size="lg" />
            </div>

            <div className="bg-white rounded-[24px] shadow-card p-6 fade-up" data-testid="verify-email-card">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-9 h-9 text-blue-500" />
                </div>
                <FormHeader title="Verifica tu correo electrónico" subtitle={`Ingresa el código de verificación de 5 dígitos que hemos enviado a ${email || 'tu correo'}.`} />

                <form onSubmit={submit}>
                    <div className="flex justify-center gap-2 my-6" onPaste={handlePaste}>
                        {digits.map((d, i) => (
                            <input
                                key={i}
                                ref={(el) => (inputs.current[i] = el)}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={d}
                                onChange={(e) => setDigit(i, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(i, e)}
                                className="w-14 h-16 bg-white border-2 border-gray-200 rounded-2xl text-2xl font-bold text-center text-brand-navy focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
                                data-testid={`verify-code-digit-${i}`}
                            />
                        ))}
                    </div>

                    {error && <div className="text-red-500 text-sm font-medium text-center mb-3" data-testid="verify-error">{error}</div>}

                    <PrimaryButton type="submit" loading={loading} testId="verify-submit-btn">Verificar</PrimaryButton>
                </form>

                <button onClick={resend} disabled={resending} className="block mx-auto mt-4 text-brand-navy font-bold hover:underline disabled:opacity-50" data-testid="verify-resend-btn">
                    {resending ? 'Enviando...' : 'Reenviar código'}
                </button>

                <button onClick={() => navigate('/intro')} className="block mx-auto mt-4 text-gray-500 text-sm hover:text-gray-700" data-testid="verify-back-home">
                    Volver al Inicio
                </button>
            </div>
        </div>
    );
}
